import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    CardItem,
    formatDuration,
    inject,
    ModalHandle,
    StatusCard,
    Tooltip,
    UserAgent,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsPencilSquare } from '@react-icons/all-files/bs/BsPencilSquare';
import { AppStore } from '../../../Core/AppStore';
import { ActiveDisplay } from '../../ActiveDisplay';
import { CommentsModal } from '../../CommentsModal';

export interface IDisplayDetailsCardProps {
    readonly display: ActiveDisplay;
    readonly loading?: boolean;
    readonly statusPoll?: Mri.DisplayStatuses.DisplayStatusPoll;
}

@autoinject
@observer
export class DisplayDetailsCard extends React.Component<IDisplayDetailsCardProps> {
    public render() {
        const { display } = this.props;
        const query = `${display.unitLocation?.latitude},${display.unitLocation?.longitude}`;
        const href = UserAgent.appleios()
            ? `https://maps.apple.com/?q=${query}`
            : `https://maps.google.com/?q=${query}`;

        return (
            <StatusCard title="General" loading={this.props.loading}>
                <CardItem
                    label="Comments"
                    value={
                        <>
                            <div className="row justify-content-between">
                                {display.comments && (
                                    <div className="col-10 ">
                                        <Tooltip message={display.comments}>
                                            <div className="ellipsis mw-300 d-block no-wrap dotted-underline">
                                                {display.comments}
                                            </div>
                                        </Tooltip>
                                    </div>
                                )}

                                {!display.comments && (
                                    <div className="col-8">
                                        <span>-</span>
                                    </div>
                                )}

                                <div className="col-2">
                                    <Tooltip message="Edit Comment">
                                        <a className="btn-link" onClick={this.openCommentsModal}>
                                            <BsPencilSquare />
                                        </a>
                                    </Tooltip>
                                </div>
                            </div>

                            {this.renderModals && (
                                <>
                                    <CommentsModal modalHandle={this.commentsModalHandle} display={display} />
                                </>
                            )}
                        </>
                    }
                />

                <CardItem label="Display ID" value={display.displayConnectionData?.customerDisplayID} />

                <CardItem label="Site ID" value={display.unitLocation?.customerSiteID} />

                <CardItem label="Model Number" value={display.modelNumber} />

                <CardItem label="Unit Serial Number" value={display.unitSerialNumber} dontRenderOnFalseValue />

                <CardItem
                    label="Unit Part Number"
                    value={
                        <span>
                            {display.unitPartNumber} Rev. {display.revision ?? '-'}
                        </span>
                    }
                />

                <CardItem
                    label="Part Number"
                    value={
                        <span>
                            {display.partNumber} Rev. {display.revision ?? '-'}
                        </span>
                    }
                />

                {this.props.statusPoll?.videoInputs?.map((v, idx) => {
                    if (!!v.xRes && !!v.yRes)
                        return (
                            <CardItem
                                key={idx}
                                label={v.name ? `${v.name} Resolution` : `Video ${idx + 1} Resolution`}
                                value={v.xRes + 'x' + v.yRes}
                            />
                        );
                })}

                {!!this.props.statusPoll?.upTime && (
                    <CardItem label="System Run Time" value={formatDuration(this.props.statusPoll.upTime)} />
                )}

                {!!this.props.statusPoll?.runTimeStatistics?.backlightOn?.sincePowerUp && (
                    <CardItem
                        label="Backlight Run Time"
                        value={formatDuration(this.props.statusPoll.runTimeStatistics.backlightOn.sincePowerUp)}
                    />
                )}

                {display.unitLocation && (
                    <>
                        <CardItem
                            label="Unit Location"
                            value={
                                <a href={href} target="_blank">
                                    {display.unitLocation.address1} {display.unitLocation.address2}{' '}
                                    {display.unitLocation.city} {display.unitLocation.stateOrProvince}{' '}
                                    {display.unitLocation.country} {display.unitLocation.postalCode}
                                </a>
                            }
                        />

                        <CardItem label="Description" value={display.unitLocation.description} />
                    </>
                )}
            </StatusCard>
        );
    }

    @bind
    @action
    private openCommentsModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.commentsModalHandle.open();
    }

    @observable
    private renderModals = false;
    private commentsModalHandle = new ModalHandle();
    @inject
    private appStore!: AppStore;
}
